import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = '/images/heritage-plane.jpg';

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [notRobot, setNotRobot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact-enquiry`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        subject: form.subject,
        message: form.message,
        consent: true,
      });
      setSubmitted(true);
      setForm(EMPTY);
      setNotRobot(false);
    } catch (err) {
      if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        setError(typeof d === 'string' ? d : 'Please check the required fields and try again.');
      } else {
        setError('Something went wrong. Please try again or email info@glossoproundtable.org directly.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting &&
    !!form.name.trim() &&
    !!form.email.trim() &&
    !!form.subject.trim() &&
    !!form.message.trim() &&
    notRobot;

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with Glossop & District Round Table 916. Based at Bradbury Community House, Market St, Glossop SK13 8AR. Email info@glossoproundtable.org."
        path="/contact"
        keywords={['contact Glossop Round Table', 'Glossop Round Table email', 'Bradbury Community House Glossop']}
      />
      <HeroSection
        image={HERO_IMG}
        title="Contact Us"
        subtitle="Drop us a line — we'd love to hear from you."
        fullHeight={false}
        alt="Glossop Round Table volunteers in orange hi-vis and Heritage Weekend t-shirts standing in front of a Spitfire display on the high street"
        captionTag="Heritage Weekend 2026"
      />

      {/* Address + email block */}
      <section className="py-14 sm:py-20 bg-white px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            data-testid="contact-address-card"
            className="bg-[#FAFAF9] border-l-4 border-[#F0A500] p-6 sm:p-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <MapPin className="w-5 h-5 text-[#F0A500]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500]">
                Our address
              </span>
            </div>
            <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">
              Glossop &amp; District Round Table 916
            </h3>
            <address className="not-italic text-base text-[#52525B] leading-relaxed">
              Bradbury Community House<br />
              Market St<br />
              Glossop<br />
              SK13 8AR
            </address>
          </div>

          <div
            data-testid="contact-email-card"
            className="bg-[#FAFAF9] border-l-4 border-[#F0A500] p-6 sm:p-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-5 h-5 text-[#F0A500]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500]">
                Email us
              </span>
            </div>
            <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">General enquiries</h3>
            <p className="text-base text-[#52525B] leading-relaxed mb-3">
              Prefer to email direct? Reach us at:
            </p>
            <a
              href="mailto:info@glossoproundtable.org"
              data-testid="contact-mailto-link"
              className="text-base font-bold text-[#F0A500] hover:text-[#D99500] break-all"
            >
              info@glossoproundtable.org
            </a>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-14 sm:py-20 bg-[#F4F4F5] px-4">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-3">
            Send a message
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-8">
            Get in touch
          </h2>

          {submitted ? (
            <div className="bg-white p-8 border-l-4 border-[#F0A500]" data-testid="contact-success">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">
                Thanks for your message!
              </h2>
              <p className="text-base text-[#52525B] leading-relaxed mb-6">
                We&rsquo;ve received your enquiry and sent a confirmation to your email. Someone from the team will come back to you as soon as we can.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                data-testid="contact-another-btn"
                className="text-sm font-bold uppercase tracking-wider text-[#F0A500] hover:text-[#D99500]"
              >
                Send another message &rarr;
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div className="bg-white p-6 sm:p-8 border border-zinc-200 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-name" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Name <span className="text-[#D62F2F]">*</span>
                    </Label>
                    <Input
                      id="c-name"
                      data-testid="contact-name"
                      required
                      value={form.name}
                      onChange={update('name')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Email <span className="text-[#D62F2F]">*</span>
                    </Label>
                    <Input
                      id="c-email"
                      data-testid="contact-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Phone <span className="text-[#71717A] font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="c-phone"
                      data-testid="contact-phone"
                      inputMode="tel"
                      value={form.phone}
                      onChange={update('phone')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-subject" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Subject <span className="text-[#D62F2F]">*</span>
                    </Label>
                    <Input
                      id="c-subject"
                      data-testid="contact-subject"
                      required
                      value={form.subject}
                      onChange={update('subject')}
                      placeholder="What's it about?"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-message" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                    Message <span className="text-[#D62F2F]">*</span>
                  </Label>
                  <Textarea
                    id="c-message"
                    data-testid="contact-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={update('message')}
                    placeholder="Tell us what we can help with."
                  />
                </div>
              </div>

              {error && (
                <p data-testid="contact-error" className="text-sm text-red-600 font-semibold">
                  {error}
                </p>
              )}

              <label
                className="flex items-center gap-3 p-4 bg-white border border-zinc-200 cursor-pointer select-none"
                data-testid="contact-not_robot-wrap"
              >
                <Checkbox
                  checked={notRobot}
                  onCheckedChange={(c) => setNotRobot(c === true)}
                  data-testid="contact-not_robot"
                />
                <span className="text-sm font-semibold text-[#0A0A0A]">I&rsquo;m not a robot</span>
              </label>

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="contact-submit"
                className="btn-amber w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : 'Send message'}
              </button>

              <p className="text-xs text-[#71717A] text-center">
                We&rsquo;ll only use your details to reply to this enquiry &mdash; no marketing, no sharing.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
